import React, { useState } from 'react';
import { OC, FONT } from '../constants/index.js';
import OCApi from '../api/client.js';
import { Button, Badge, Input, Card } from '../components/ui.js';

const GUIDE_TOPICS = ['applications', 'scholarships', 'admissions', 'interviews', 'internships', 'events'];
const EMPTY_CHANNEL = { region: '', handle: '', platform: 'WhatsApp', joinUrl: '', memberCountDisplay: '', sortOrder: 0, isActive: true };
const EMPTY_GUIDE = { slug: '', title: '', excerpt: '', body: '', topic: 'scholarships', readMinutes: 5, status: 'draft', authorLabel: 'OC Editorial' };
const EMPTY_TESTIMONIAL = { quote: '', personName: '', roleLabel: '', sortOrder: 0, isActive: true };
const EMPTY_CHAPTER = { name: '', region: '', universityName: '', description: '', memberCountDisplay: '', contactUrl: '', sortOrder: 0, isActive: true };

function AdminContentTab({
    s, t, cardStyle, adminInputStyle, loadAll, busyId, setBusyId,
    communityChannels, adminGuides, reviewerApps, newsletterSubscribers,
    adminTestimonials, adminChapters,
}) {
    const [channelForm, setChannelForm] = useState(null);
    const [guideForm, setGuideForm] = useState(null);
    const [testimonialForm, setTestimonialForm] = useState(null);
    const [chapterForm, setChapterForm] = useState(null);

    const toast = (msg, icon = 'check') => s.showToast(msg, icon);

    const saveChannel = async () => {
        if (!channelForm?.region || !channelForm?.joinUrl) {
            toast('Region and join URL required', 'x');
            return;
        }
        setBusyId('save-channel');
        try {
            if (channelForm.id) {
                await OCApi.patchAdminCommunityChannel(channelForm.id, channelForm);
            } else {
                await OCApi.createAdminCommunityChannel(channelForm);
            }
            setChannelForm(null);
            await loadAll();
            toast('Channel saved');
        } catch (err) {
            toast(err.message || 'Save failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const saveGuide = async () => {
        if (!guideForm?.slug || !guideForm?.title || !guideForm?.body) {
            toast('Slug, title, and body required', 'x');
            return;
        }
        setBusyId('save-guide');
        try {
            const payload = { ...guideForm, readMinutes: Number(guideForm.readMinutes) || null };
            if (guideForm.id) {
                await OCApi.patchAdminResourceGuide(guideForm.id, payload);
            } else {
                await OCApi.createAdminResourceGuide(payload);
            }
            setGuideForm(null);
            await loadAll();
            toast('Guide saved');
        } catch (err) {
            toast(err.message || 'Save failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const saveTestimonial = async () => {
        if (!testimonialForm?.quote || !testimonialForm?.personName) {
            toast('Quote and name required', 'x');
            return;
        }
        setBusyId('save-testimonial');
        try {
            if (testimonialForm.id) {
                await OCApi.patchAdminTestimonial(testimonialForm.id, testimonialForm);
            } else {
                await OCApi.createAdminTestimonial(testimonialForm);
            }
            setTestimonialForm(null);
            await loadAll();
            toast('Testimonial saved');
        } catch (err) {
            toast(err.message || 'Save failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const saveChapter = async () => {
        if (!chapterForm?.name) {
            toast('Chapter name required', 'x');
            return;
        }
        setBusyId('save-chapter');
        try {
            if (chapterForm.id) {
                await OCApi.patchAdminUniversityChapter(chapterForm.id, chapterForm);
            } else {
                await OCApi.createAdminUniversityChapter(chapterForm);
            }
            setChapterForm(null);
            await loadAll();
            toast('Chapter saved');
        } catch (err) {
            toast(err.message || 'Save failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const field = (label, value, onChange, opts = {}) => React.createElement('div', { style: { marginBottom: 10 } },
        React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: t.text.secondary, marginBottom: 4 } }, label),
        opts.multiline
            ? React.createElement('textarea', {
                value: value || '',
                onChange: e => onChange(e.target.value),
                rows: opts.rows || 4,
                style: { ...adminInputStyle, minHeight: 80, resize: 'vertical' },
            })
            : React.createElement('input', {
                type: opts.type || 'text',
                value: value ?? '',
                onChange: e => onChange(opts.type === 'number' ? Number(e.target.value) : e.target.value),
                style: adminInputStyle,
            })
    );

    return React.createElement('div', null,
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
            React.createElement('div', { style: { fontSize: 14, fontWeight: 700, color: t.text.primary } }, `Regional channels (${communityChannels.length})`),
            React.createElement(Button, { kind: 'secondary', size: 'sm', theme: s.theme, onClick: () => setChannelForm({ ...EMPTY_CHANNEL }) }, 'Add channel')
        ),
        channelForm ? React.createElement('div', { style: { ...cardStyle, marginBottom: 12 } },
            field('Region', channelForm.region, v => setChannelForm(f => ({ ...f, region: v }))),
            field('Handle / subtitle', channelForm.handle, v => setChannelForm(f => ({ ...f, handle: v }))),
            field('Platform', channelForm.platform, v => setChannelForm(f => ({ ...f, platform: v }))),
            field('Join URL', channelForm.joinUrl, v => setChannelForm(f => ({ ...f, joinUrl: v }))),
            field('Member count display', channelForm.memberCountDisplay, v => setChannelForm(f => ({ ...f, memberCountDisplay: v }))),
            field('Sort order', channelForm.sortOrder, v => setChannelForm(f => ({ ...f, sortOrder: v })), { type: 'number' }),
            React.createElement('div', { style: { display: 'flex', gap: 8, marginTop: 8 } },
                React.createElement(Button, { kind: 'primary', size: 'sm', theme: s.theme, onClick: saveChannel, disabled: busyId === 'save-channel' }, 'Save'),
                React.createElement(Button, { kind: 'ghost', size: 'sm', theme: s.theme, onClick: () => setChannelForm(null) }, 'Cancel')
            )
        ) : null,
        communityChannels.map(ch =>
            React.createElement('div', { key: ch.id, style: { ...cardStyle, marginBottom: 10, fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' } },
                React.createElement('div', null,
                    React.createElement('strong', null, ch.region),
                    ' · ', ch.platform, ' · ', ch.joinUrl,
                    ch.isActive ? '' : ' (inactive)'
                ),
                React.createElement(Button, {
                    kind: 'ghost', size: 'sm', theme: s.theme,
                    onClick: () => setChannelForm({ ...ch }),
                }, 'Edit')
            )
        ),

        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 10px' } },
            React.createElement('div', { style: { fontSize: 14, fontWeight: 700, color: t.text.primary } }, `University chapters (${adminChapters.length})`),
            React.createElement(Button, { kind: 'secondary', size: 'sm', theme: s.theme, onClick: () => setChapterForm({ ...EMPTY_CHAPTER }) }, 'Add chapter')
        ),
        chapterForm ? React.createElement('div', { style: { ...cardStyle, marginBottom: 12 } },
            field('Name', chapterForm.name, v => setChapterForm(f => ({ ...f, name: v }))),
            field('Region', chapterForm.region, v => setChapterForm(f => ({ ...f, region: v }))),
            field('University', chapterForm.universityName, v => setChapterForm(f => ({ ...f, universityName: v }))),
            field('Description', chapterForm.description, v => setChapterForm(f => ({ ...f, description: v })), { multiline: true, rows: 2 }),
            field('Members display', chapterForm.memberCountDisplay, v => setChapterForm(f => ({ ...f, memberCountDisplay: v }))),
            field('Contact URL', chapterForm.contactUrl, v => setChapterForm(f => ({ ...f, contactUrl: v }))),
            field('Sort order', chapterForm.sortOrder, v => setChapterForm(f => ({ ...f, sortOrder: v })), { type: 'number' }),
            React.createElement('div', { style: { display: 'flex', gap: 8, marginTop: 8 } },
                React.createElement(Button, { kind: 'primary', size: 'sm', theme: s.theme, onClick: saveChapter, disabled: busyId === 'save-chapter' }, 'Save'),
                React.createElement(Button, { kind: 'ghost', size: 'sm', theme: s.theme, onClick: () => setChapterForm(null) }, 'Cancel')
            )
        ) : null,
        adminChapters.map(ch =>
            React.createElement('div', { key: ch.id, style: { ...cardStyle, marginBottom: 8, fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 8 } },
                React.createElement('div', null,
                    React.createElement('strong', null, ch.name),
                    ch.universityName ? ` · ${ch.universityName}` : '',
                    ch.memberCountDisplay ? ` · ${ch.memberCountDisplay} members` : ''
                ),
                React.createElement(Button, { kind: 'ghost', size: 'sm', theme: s.theme, onClick: () => setChapterForm({ ...ch }) }, 'Edit')
            )
        ),

        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 10px' } },
            React.createElement('div', { style: { fontSize: 14, fontWeight: 700, color: t.text.primary } }, `Testimonials (${adminTestimonials.length})`),
            React.createElement(Button, { kind: 'secondary', size: 'sm', theme: s.theme, onClick: () => setTestimonialForm({ ...EMPTY_TESTIMONIAL }) }, 'Add testimonial')
        ),
        testimonialForm ? React.createElement('div', { style: { ...cardStyle, marginBottom: 12 } },
            field('Quote', testimonialForm.quote, v => setTestimonialForm(f => ({ ...f, quote: v })), { multiline: true, rows: 3 }),
            field('Person name', testimonialForm.personName, v => setTestimonialForm(f => ({ ...f, personName: v }))),
            field('Role label', testimonialForm.roleLabel, v => setTestimonialForm(f => ({ ...f, roleLabel: v }))),
            field('Sort order', testimonialForm.sortOrder, v => setTestimonialForm(f => ({ ...f, sortOrder: v })), { type: 'number' }),
            React.createElement('div', { style: { display: 'flex', gap: 8, marginTop: 8 } },
                React.createElement(Button, { kind: 'primary', size: 'sm', theme: s.theme, onClick: saveTestimonial, disabled: busyId === 'save-testimonial' }, 'Save'),
                React.createElement(Button, { kind: 'ghost', size: 'sm', theme: s.theme, onClick: () => setTestimonialForm(null) }, 'Cancel')
            )
        ) : null,
        adminTestimonials.map(tm =>
            React.createElement('div', { key: tm.id, style: { ...cardStyle, marginBottom: 8, fontSize: 13 } },
                React.createElement('div', { style: { fontStyle: 'italic', marginBottom: 6 } }, `"${tm.quote}"`),
                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 8 } },
                    React.createElement('span', null, tm.personName, tm.roleLabel ? ` · ${tm.roleLabel}` : '', tm.isActive ? '' : ' (hidden)'),
                    React.createElement(Button, { kind: 'ghost', size: 'sm', theme: s.theme, onClick: () => setTestimonialForm({ ...tm }) }, 'Edit')
                )
            )
        ),

        React.createElement('div', { style: { fontSize: 14, fontWeight: 700, color: t.text.primary, margin: '24px 0 10px' } }, `Reviewer applications (${reviewerApps.length})`),
        reviewerApps.length ? reviewerApps.map(app =>
            React.createElement('div', {
                key: app.id,
                style: { ...cardStyle, marginBottom: 10, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }
            },
                React.createElement('div', null,
                    React.createElement('div', { style: { fontWeight: 700 } }, app.fullName),
                    React.createElement('div', { style: { fontSize: 12, color: t.text.secondary } }, `${app.email} · ${app.chapter || '—'}`)
                ),
                React.createElement('div', { style: { display: 'flex', gap: 8 } },
                    React.createElement(Button, {
                        kind: 'primary', size: 'sm', theme: s.theme,
                        disabled: busyId === `rev-${app.id}`,
                        onClick: async () => {
                            setBusyId(`rev-${app.id}`);
                            try {
                                await OCApi.updateAdminReviewerApplication(app.id, { status: 'approved' });
                                await loadAll();
                                toast('Reviewer approved');
                            } finally { setBusyId(null); }
                        },
                    }, 'Approve'),
                    React.createElement(Button, {
                        kind: 'ghost', size: 'sm', theme: s.theme,
                        disabled: busyId === `rev-${app.id}`,
                        onClick: async () => {
                            setBusyId(`rev-${app.id}`);
                            try {
                                await OCApi.updateAdminReviewerApplication(app.id, { status: 'rejected' });
                                await loadAll();
                            } finally { setBusyId(null); }
                        },
                    }, 'Reject')
                )
            )
        ) : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary } }, 'No pending reviewer applications.'),

        React.createElement('div', { style: { fontSize: 14, fontWeight: 700, color: t.text.primary, margin: '24px 0 10px' } }, `Newsletter subscribers (${newsletterSubscribers.length})`),
        newsletterSubscribers.length ? React.createElement(Card, { theme: s.theme, padding: 0, style: { overflow: 'hidden', marginBottom: 16 } },
            React.createElement('div', { style: { overflowX: 'auto' } },
                React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontFamily: FONT.body } },
                    React.createElement('thead', null,
                        React.createElement('tr', { style: { background: t.bg.subtle, borderBottom: `1px solid ${t.border.base}` } },
                            ['Email', 'Source', 'Confirmed', 'Subscribed'].map(h =>
                                React.createElement('th', { key: h, style: { padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: t.text.secondary, textTransform: 'uppercase' } }, h)
                            )
                        )
                    ),
                    React.createElement('tbody', null,
                        newsletterSubscribers.map(sub =>
                            React.createElement('tr', { key: sub.id, style: { borderBottom: `1px solid ${t.border.base}` } },
                                React.createElement('td', { style: { padding: '10px 14px', fontSize: 13, color: t.text.primary } }, sub.email),
                                React.createElement('td', { style: { padding: '10px 14px', fontSize: 13, color: t.text.secondary } }, sub.source || 'website'),
                                React.createElement('td', { style: { padding: '10px 14px', fontSize: 13, color: t.text.secondary } },
                                    sub.confirmedAt ? new Date(sub.confirmedAt).toLocaleString() : '—'
                                ),
                                React.createElement('td', { style: { padding: '10px 14px', fontSize: 13, color: t.text.secondary } },
                                    sub.createdAt ? new Date(sub.createdAt).toLocaleString() : '—'
                                )
                            )
                        )
                    )
                )
            )
        ) : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary, marginBottom: 16 } }, 'No active newsletter subscribers yet.'),

        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 10px' } },
            React.createElement('div', { style: { fontSize: 14, fontWeight: 700, color: t.text.primary } }, `Resource guides (${adminGuides.length})`),
            React.createElement(Button, { kind: 'secondary', size: 'sm', theme: s.theme, onClick: () => setGuideForm({ ...EMPTY_GUIDE }) }, 'New guide')
        ),
        guideForm ? React.createElement('div', { style: { ...cardStyle, marginBottom: 12 } },
            field('Slug', guideForm.slug, v => setGuideForm(f => ({ ...f, slug: v }))),
            field('Title', guideForm.title, v => setGuideForm(f => ({ ...f, title: v }))),
            field('Excerpt', guideForm.excerpt, v => setGuideForm(f => ({ ...f, excerpt: v })), { multiline: true, rows: 2 }),
            React.createElement('div', { style: { marginBottom: 10 } },
                React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: t.text.secondary, marginBottom: 4 } }, 'Topic'),
                React.createElement('select', {
                    value: guideForm.topic,
                    onChange: e => setGuideForm(f => ({ ...f, topic: e.target.value })),
                    style: adminInputStyle,
                }, GUIDE_TOPICS.map(tp => React.createElement('option', { key: tp, value: tp }, tp)))
            ),
            field('Read minutes', guideForm.readMinutes, v => setGuideForm(f => ({ ...f, readMinutes: v })), { type: 'number' }),
            field('Author label', guideForm.authorLabel, v => setGuideForm(f => ({ ...f, authorLabel: v }))),
            React.createElement('div', { style: { marginBottom: 10 } },
                React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: t.text.secondary, marginBottom: 4 } }, 'Status'),
                React.createElement('select', {
                    value: guideForm.status,
                    onChange: e => setGuideForm(f => ({ ...f, status: e.target.value })),
                    style: adminInputStyle,
                },
                    React.createElement('option', { value: 'draft' }, 'draft'),
                    React.createElement('option', { value: 'published' }, 'published')
                )
            ),
            field('Body (markdown)', guideForm.body, v => setGuideForm(f => ({ ...f, body: v })), { multiline: true, rows: 8 }),
            React.createElement('div', { style: { display: 'flex', gap: 8, marginTop: 8 } },
                React.createElement(Button, { kind: 'primary', size: 'sm', theme: s.theme, onClick: saveGuide, disabled: busyId === 'save-guide' }, 'Save guide'),
                React.createElement(Button, { kind: 'ghost', size: 'sm', theme: s.theme, onClick: () => setGuideForm(null) }, 'Cancel')
            )
        ) : null,
        adminGuides.map(g =>
            React.createElement('div', {
                key: g.id,
                style: { ...cardStyle, marginBottom: 10, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }
            },
                React.createElement('div', null,
                    React.createElement('div', { style: { fontWeight: 700 } }, g.title),
                    React.createElement('div', { style: { fontSize: 12, color: t.text.secondary } },
                        `${g.slug} · ${g.status} · ${g.viewCount ?? 0} views · ${g.searchHitCount ?? 0} searches`
                    )
                ),
                React.createElement('div', { style: { display: 'flex', gap: 8 } },
                    React.createElement(Button, { kind: 'ghost', size: 'sm', theme: s.theme, onClick: () => setGuideForm({ ...g }) }, 'Edit'),
                    g.status !== 'published' ? React.createElement(Button, {
                        kind: 'secondary', size: 'sm', theme: s.theme,
                        onClick: async () => {
                            await OCApi.patchAdminResourceGuide(g.id, { status: 'published' });
                            await loadAll();
                            toast('Guide published');
                        },
                    }, 'Publish') : React.createElement(Badge, { color: OC.success, theme: s.theme }, 'Live')
                )
            )
        )
    );
}

export { AdminContentTab };
